import { Card } from '../components/atoms/Card';
import { LayoutContainer } from '../components/atoms/LayoutContainer';
import { LayoutFlow } from '../components/atoms/LayoutFlow';
import { LayoutFlowItem } from '../components/atoms/LayoutFlowItem';
import { LayoutImage } from '../components/atoms/LayoutImage';
import { LayoutTable } from 'components/atoms/LayoutTable';
import { LayoutText } from '../components/atoms/LayoutText';
import React from 'react';
import { Spacer } from '../components/atoms/Spacer';
import client from '../tina/__generated__/client';
import { Exact, ServicesAndPricesQuery } from '../tina/__generated__/types';
import { useTina } from 'tinacms/dist/react';
import { Content9 } from 'components/organisms/Content9/Content9';
import { config, styled } from '../stitches.config';
import { CopyText, Heading } from 'shared/Typography';
import { Section } from 'react-headings';
import { SvgLink } from '../components/atoms/svg/SvgLink';
import { Flex } from 'components/atoms/Flex';
import { ContentCenter14 } from '../components/organisms/ContentCenter14/ContentCenter14';
import { MarkdownWithTypo } from '../components/organisms/MarkdownWithTypo/MarkdownWithTypo';
import { Anchor } from '../components/atoms/Anchor/Anchor';
import { NextSeo, NextSeoProps } from 'next-seo';

export const ScrollableContainer = styled('div', {
  display: 'flex',
  overflow: 'auto',
});

export default function ServicesAndPrices(props: {
  data: ServicesAndPricesQuery;
  query: string;
  variables: Exact<{ relativePath: string }>;
}) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  return (
    <>
      <NextSeo {...(data.servicesAndPrices.seo as NextSeoProps)} />
      <Flex
        css={{
          backgroundColor: '$skin-white-smoke',
          flexDirection: 'column',
          paddingBlock: '$spacing-5x',
          paddingInline: '$spacingMainMobile',
          '@tabletAndUp': {
            paddingBlock: '$spacing-7x',
          },
          '@laptopAndUp': {
            display: 'grid', // Necessary for Safari
          },
        }}
      >
        <Flex
          css={{
            flexDirection: 'column',
            maxWidth: '$laptop',
            margin: 'auto',
            '@laptopAndUp': {
              display: 'grid', // Necessary for Safari
            },
          }}
        >
          <Section
            component={
              <Heading
                variant={{ '@initial': 'h2', '@tabletAndUp': 'h2TabletAndUp' }}
              >
                {data.servicesAndPrices.headline}
              </Heading>
            }
          >
            <Flex
              css={{
                alignItems: 'center',
                flexDirection: 'column',
                gap: '$spacing-5x',
                paddingBlock: '$spacing-5x',
                '@tabletAndUp': {
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                  alignItems: 'stretch',
                  paddingBlock: '$spacing-10x',
                },
                '@laptopAndUp': {
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
                },
              }}
            >
              {data.servicesAndPrices.cards?.map((card, index) => (
                <Card
                  linkUrl={card?.link}
                  scroll={false}
                  key={'cardService' + index}
                >
                  <LayoutImage
                    key={'cardServiceImage' + index}
                    aspectRatio={41 / 25}
                    imageAlt={card?.imageAlt}
                    imageUrl={card?.imageSrc}
                    imagePosition={card?.imagePosition}
                    sizes={`${config.media.tabletAndUp} 15vw, 100vw`}
                  />
                  <LayoutContainer
                    paddingBlock="$spacing-4x"
                    paddingInline="$spacing-4x"
                  >
                    <LayoutFlow align="center" gap="$spacing-4x" wrap="wrap">
                      <LayoutFlowItem>
                        <Heading
                          color="skinAccent1Shade1"
                          variant="h5"
                          align="center"
                        >
                          {card?.headline}
                        </Heading>
                      </LayoutFlowItem>
                      <LayoutFlowItem>
                        <LayoutText align="center" lineHeight="$m">
                          {card?.content}
                        </LayoutText>
                      </LayoutFlowItem>
                    </LayoutFlow>
                  </LayoutContainer>
                </Card>
              ))}
            </Flex>
          </Section>
        </Flex>
      </Flex>

      <Flex
        css={{
          flexDirection: 'column',
          paddingInline: '$spacingMainMobile',
        }}
      >
        <Flex
          css={{
            flexDirection: 'column',
            maxWidth: '$laptop',
            margin: 'auto',
            gap: '$spacing-5x',
            alignContent: 'stretch',
            justifyContent: 'center',
            '@tabletAndUp': {
              gap: '$spacing-10x',
              paddingBlock: '$spacing-8x',
            },
            '@laptopAndUp': {
              gap: '$spacing-15x',
            },
            '@desktopAndUp': {
              gap: '$spacing-1px',
            },
          }}
        >
          {data.servicesAndPrices.contentComponents?.map(
            (contentComponent, index) => (
              <Content9
                title={contentComponent?.headline}
                buttonHref={contentComponent?.buttonHref}
                buttonLabel={contentComponent?.buttonLabel}
                imageSrc={contentComponent?.imageSrc}
                imageAlt={contentComponent?.imageAlt}
                imagePosition={contentComponent?.imagePosition ?? undefined}
                textPosition={
                  contentComponent?.textPosition === 'left' ? 'left' : 'right'
                }
                key={'content9-' + index}
              >
                <MarkdownWithTypo content={contentComponent?.content} />
              </Content9>
            ),
          )}
        </Flex>
      </Flex>

      <Flex
        css={{
          flexDirection: 'column',
          paddingInline: '$spacingMainMobile',
          paddingBlock: '$spacing-5x',
          '@tabletAndUp': {
            paddingBlock: 0,
          },
        }}
      >
        <Flex
          css={{
            flexDirection: 'column',
            maxWidth: '$laptop',
            margin: 'auto',
            gap: '$spacing-5x',
            alignContent: 'stretch',
            justifyContent: 'center',
            width: '100%',
            '@tabletAndUp': {
              gap: '$spacing-5x',
              paddingBlock: '$spacing-8x',
            },
          }}
        >
          <Section
            component={
              <Heading
                variant={{ '@initial': 'h2', '@tabletAndUp': 'h2TabletAndUp' }}
              >
                {data.servicesAndPrices.headlineFieldOfExpertise}
              </Heading>
            }
          >
            <MarkdownWithTypo
              content={data.servicesAndPrices.textFieldOfExpertise}
            />
            <Flex
              css={{
                flexDirection: 'column',
                gap: '$spacing-4x',
                '@tabletAndUp': {
                  flexDirection: 'row',
                  gap: '$spacing-3x',
                },
              }}
            >
              {data.servicesAndPrices.fieldOfExpertises?.map(
                (fieldOfExpertise, index) => (
                  <Flex
                    key={'fieldOfExpertise-' + index}
                    css={{
                      flexDirection: 'column',
                      gap: '$spacing-2x',
                      '@tabletAndUp': {
                        maxWidth: 'calc($laptop / 3)',
                      },
                    }}
                  >
                    <Section
                      component={
                        <a
                          href={fieldOfExpertise?.href ?? undefined}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Heading
                            color="skinAccent1Shade1"
                            variant={{
                              '@initial': 'h6',
                              '@tabletAndUp': 'h6',
                            }}
                          >
                            <SvgLink /> {fieldOfExpertise?.headline}
                          </Heading>
                        </a>
                      }
                    >
                      <CopyText>{fieldOfExpertise?.content}</CopyText>
                    </Section>
                  </Flex>
                ),
              )}
            </Flex>
          </Section>
        </Flex>
      </Flex>

      <ContentCenter14
        content={data.servicesAndPrices?.scheduleMeetingContent}
        label={data.servicesAndPrices?.scheduleMeetingLinkLabel}
        href={data.servicesAndPrices?.scheduleMeetingLinkHref}
      />

      <Anchor id="preisliste" />
      <LayoutContainer
        maxContentWidth="$laptop"
        paddingBlock="$spacing-7x"
        paddingInline="$spacing-2x"
      >
        <Section
          component={
            <Heading
              variant={{ '@initial': 'h2', '@tabletAndUp': 'h2TabletAndUp' }}
            >
              {data.servicesAndPrices.headlinePriceList}
            </Heading>
          }
        >
          <Spacer size="$spacing-7x" />
          <ScrollableContainer>
            <LayoutTable
              body={data.servicesAndPrices.pricelist?.map(
                (priceListEntry, index) => {
                  return {
                    id: 'body' + index,
                    values: new Map([
                      ['head1', priceListEntry?.service ?? ''],
                      ['head2', priceListEntry?.withoutVat ?? ''],
                      ['head3', priceListEntry?.withVatAustria ?? ''],
                      ['head4', priceListEntry?.withVatGermany ?? ''],
                    ]),
                  };
                },
              )}
              head={[
                { id: 'head1', label: 'Leistung' },
                { id: 'head2', label: 'exkl. USt.' },
                { id: 'head3', label: 'inkl. USt. (Österreich)' },
                { id: 'head4', label: 'inkl. USt. (Deutschland)' },
              ]}
            />
          </ScrollableContainer>
        </Section>
      </LayoutContainer>

      <ContentCenter14
        content={data.servicesAndPrices?.pricelistDownloadContent}
        label={data.servicesAndPrices?.pricelistDownloadLinkLabel}
        href={data.servicesAndPrices?.pricelistDownloadLinkHref}
      />
    </>
  );
}

export const getStaticProps = async () => {
  const pageResponse = await client.queries.servicesAndPrices({
    relativePath: 'services-and-prices.mdx',
  });

  return {
    props: {
      data: pageResponse.data,
      query: pageResponse.query,
      variables: pageResponse.variables,
    },
  };
};
